/**
 * AdReadyCard — Director chat question variant ``ad_ready``.
 *
 * Rendered by QuestionStack when ``question.question_type === 'ad_ready'``.
 * Shows the rendered ad inline with cost/duration metadata and two CTAs:
 * - Approve   → answer = "approve"
 * - Regenerate → answer = "regenerate"
 *
 * UX guideline: cost is displayed in **sparks** (credits) only — never raw
 * USD. See CLAUDE.md "Credits Display".
 *
 * Build spec ref: docs/drafts/ai-ads-phase-0-1-build-spec.md §11
 * Backend builder: ``ads_tools.build_ad_ready_question``
 */

import type { EmbeddedQuestion } from '@console/hooks/useAgentChat'

interface AdReadyMeta {
  video_url?: string
  duration_s?: number
  cost_sparks?: number
  aspect?: string
  poster_url?: string | null
}

function readMeta(question: EmbeddedQuestion): AdReadyMeta {
  const m = question.metadata
  if (!m) return {}
  return {
    video_url: m.video_url,
    duration_s: m.duration_s,
    cost_sparks: m.cost_sparks,
    aspect: m.aspect,
    poster_url: m.poster_url,
  }
}

export function AdReadyCard({
  question,
  selected,
  readOnly,
  onAnswer,
}: {
  question: EmbeddedQuestion
  selected: string | undefined
  readOnly: boolean
  onAnswer: (value: string) => void
}) {
  const meta = readMeta(question)

  return (
    <div
      className="rounded-md border border-line-2 bg-bg-2/60 px-3.5 py-3"
      data-testid="ad-ready-card"
    >
      {question.header && (
        <div className="t-mono text-[0.62rem] uppercase tracking-[0.18em] text-accent mb-1">
          {question.header}
        </div>
      )}
      <div className="text-text text-[0.92rem] leading-snug mb-2.5">
        {question.question}
      </div>

      {meta.video_url ? (
        <div className="rounded-md border border-line-2 overflow-hidden bg-black mb-2.5">
          <video
            src={meta.video_url}
            controls
            preload="metadata"
            poster={meta.poster_url ?? undefined}
            className="block w-full h-auto"
            style={{
              maxHeight: 360,
              aspectRatio: meta.aspect === '9:16' ? '9 / 16' : undefined,
              objectFit: 'contain',
            }}
            data-testid="ad-ready-video"
          />
        </div>
      ) : (
        <div className="rounded-md border border-line-2 bg-white/[0.02] px-3.5 py-3 text-text-3 text-[0.82rem] italic mb-2.5">
          No video URL provided.
        </div>
      )}

      <div className="flex items-center gap-2 mb-2.5 text-text-3 text-[0.78rem] t-mono">
        {typeof meta.duration_s === 'number' && (
          <span data-testid="ad-ready-duration">
            {meta.duration_s.toFixed(1)}s
          </span>
        )}
        {typeof meta.duration_s === 'number' &&
          typeof meta.cost_sparks === 'number' && (
            <span aria-hidden>·</span>
          )}
        {typeof meta.cost_sparks === 'number' && (
          <span data-testid="ad-ready-cost">{meta.cost_sparks} credits</span>
        )}
        {meta.aspect && (
          <>
            <span aria-hidden>·</span>
            <span data-testid="ad-ready-aspect">{meta.aspect}</span>
          </>
        )}
      </div>

      {/* CTA buttons. Same selected/readOnly contract as the default
          QuestionCard so the auto-submit timing in QuestionStack works
          uniformly across variants. */}
      <div className="flex flex-col gap-1.5">
        {question.options.map((opt, i) => {
          const value = opt.value ?? opt.label
          const isSelected = selected === value
          const isApprove = value === 'approve'
          return (
            <button
              key={`${value}-${i}`}
              type="button"
              disabled={readOnly}
              onClick={readOnly ? undefined : () => onAnswer(value)}
              data-testid={`ad-ready-${value}`}
              className={`group text-left px-3.5 py-2.5 rounded-md border transition-all relative ${
                isSelected
                  ? 'border-accent bg-accent/[0.06] shadow-[0_0_0_1px_var(--accent)_inset]'
                  : readOnly
                    ? 'border-line-2 bg-white/[0.02] opacity-50 cursor-default'
                    : isApprove
                      ? 'border-line-2 bg-accent/[0.04] hover:bg-accent/[0.08] hover:border-accent/60'
                      : 'border-line-2 bg-white/[0.02] hover:bg-white/[0.04] hover:border-line-3'
              }`}
            >
              <div
                className={`text-[0.95rem] leading-tight ${
                  isSelected ? 'text-accent' : 'text-text'
                }`}
                style={{
                  fontVariationSettings: '"wdth" 96, "opsz" 96',
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                }}
              >
                {opt.label}
              </div>
              {opt.description && (
                <div
                  className={`mt-0.5 text-[0.82rem] leading-snug ${
                    isSelected ? 'text-accent/70' : 'text-text-3'
                  }`}
                >
                  {opt.description}
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
